import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock, faTimes, faUser } from '@fortawesome/free-solid-svg-icons';
import { customerApi, servicesLoVApi, staffApi } from '@api/api';
import {
  CreateWaitingListBody,
  WaitingListItem,
  createWaitingList,
  isExactSlot,
  trimTime,
  updateWaitingList,
} from '@/features/waitingList/api';
import { useToast } from '@/hooks/useToast';

type Mode = 'create' | 'edit';
type SlotMode = 'exact' | 'range';

interface Prefill {
  customerId?: string;
  customerLabel?: string;
  serviceId?: string;
  preferredWorkerId?: string;
  dateFrom?: string;
  dateTo?: string;
  timeFrom?: string;
  timeTo?: string;
  notes?: string;
  priority?: number;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSaved?: (item: WaitingListItem) => void;
  salonId: string;
  mode?: Mode;
  /** Použito v edit režimu nebo pro prefill z create flow. */
  existing?: WaitingListItem;
  /** Pro create flow z WhatsApp/customer detailu — prefilluje pole. */
  prefill?: Prefill;
}

interface Option {
  id: string;
  name: string;
  hint?: string;
}

const todayISO = () => dayjs().format('YYYY-MM-DD');
const inDays = (n: number) => dayjs().add(n, 'day').format('YYYY-MM-DD');

const WaitingListModal: React.FC<Props> = ({ isOpen, onClose, onSaved, salonId, mode = 'create', existing, prefill }) => {
  const toast = useToast();

  const [services, setServices] = useState<Option[]>([]);
  const [staff, setStaff] = useState<Option[]>([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerResults, setCustomerResults] = useState<Option[]>([]);
  const [customerLoading, setCustomerLoading] = useState(false);

  const [form, setForm] = useState({
    customerId: '',
    customerLabel: '',
    serviceId: '',
    preferredWorkerId: '',
    slotMode: 'exact' as SlotMode,
    date: todayISO(),
    time: '09:00',
    dateFrom: todayISO(),
    dateTo: inDays(7),
    timeFrom: '',
    timeTo: '',
    notes: '',
    priority: 0,
  });

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize form on open / when existing/prefill changes
  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    if (mode === 'edit' && existing) {
      const exact = isExactSlot(existing);
      setForm({
        customerId: existing.customer.id,
        customerLabel: `${existing.customer.firstName} ${existing.customer.lastName}`.trim(),
        serviceId: existing.service.id,
        preferredWorkerId: existing.preferredWorker?.id ?? '',
        slotMode: exact ? 'exact' : 'range',
        date: existing.dateFrom,
        time: trimTime(existing.timeFrom) || '09:00',
        dateFrom: existing.dateFrom,
        dateTo: existing.dateTo,
        timeFrom: trimTime(existing.timeFrom),
        timeTo: trimTime(existing.timeTo),
        notes: existing.notes ?? '',
        priority: existing.priority,
      });
    } else {
      setForm({
        customerId: prefill?.customerId ?? '',
        customerLabel: prefill?.customerLabel ?? '',
        serviceId: prefill?.serviceId ?? '',
        preferredWorkerId: prefill?.preferredWorkerId ?? '',
        slotMode: 'exact',
        date: prefill?.dateFrom ?? todayISO(),
        time: prefill?.timeFrom ?? '09:00',
        dateFrom: prefill?.dateFrom ?? todayISO(),
        dateTo: prefill?.dateTo ?? inDays(7),
        timeFrom: prefill?.timeFrom ?? '',
        timeTo: prefill?.timeTo ?? '',
        notes: prefill?.notes ?? '',
        priority: prefill?.priority ?? 0,
      });
    }
  }, [isOpen, mode, existing, prefill]);

  // Load services + staff when modal opens
  useEffect(() => {
    if (!isOpen) return;
    servicesLoVApi
      .getActiveServicesLoV({ salonId })
      .then(r => setServices(r.data.map((s: any) => ({ id: s.id, name: s.name }))))
      .catch(() => undefined);
    staffApi
      .getActiveStaffLoV({ salonId })
      .then(r => setStaff(r.data.map((s: any) => ({ id: s.id, name: s.name || s.displayName }))))
      .catch(() => undefined);
  }, [isOpen, salonId]);

  // Customer search debounce
  useEffect(() => {
    if (!isOpen || !customerSearch.trim() || form.customerId) return;
    setCustomerLoading(true);
    const t = setTimeout(() => {
      customerApi
        .getCustomers({ query: customerSearch, page: 0, size: 8 })
        .then(r =>
          setCustomerResults(
            (r.data.content ?? []).map((c: any) => ({
              id: c.id,
              name: `${c.firstName} ${c.lastName}`.trim(),
              hint: c.phone || c.email,
            }))
          )
        )
        .catch(() => setCustomerResults([]))
        .finally(() => setCustomerLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [customerSearch, isOpen, form.customerId]);

  const canSubmit = useMemo(
    () => Boolean(form.customerId && form.serviceId && form.dateFrom && form.dateTo),
    [form]
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const body: CreateWaitingListBody = {
        customerId: form.customerId,
        serviceId: form.serviceId,
        dateFrom: form.slotMode === 'exact' ? form.date : form.dateFrom,
        dateTo: form.slotMode === 'exact' ? form.date : form.dateTo,
        timeFrom: form.slotMode === 'exact' ? form.time : form.timeFrom || undefined,
        timeTo: form.slotMode === 'exact' ? form.time : form.timeTo || undefined,
        notes: form.notes || undefined,
        priority: form.priority,
        preferredWorkerId: form.preferredWorkerId || undefined,
      };

      const saved =
        mode === 'edit' && existing
          ? await updateWaitingList(salonId, existing.id, body)
          : await createWaitingList(salonId, body);

      toast.success(
        mode === 'edit' ? 'Záznam upraven' : 'Přidáno na čekací listinu',
        `${form.customerLabel || 'Klient'} — ${services.find(s => s.id === form.serviceId)?.name ?? ''}`
      );
      onSaved?.(saved);
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e?.message ?? 'Chyba ukládání');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <FontAwesomeIcon icon={faClock} className="text-amber-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">
              {mode === 'edit' ? 'Upravit záznam' : 'Přidat na čekací listinu'}
            </h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <FontAwesomeIcon icon={faTimes} className="text-xl" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Customer */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Klient *</label>
            {form.customerId ? (
              <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                <div className="flex items-center">
                  <FontAwesomeIcon icon={faUser} className="text-gray-400 mr-2" />
                  <span className="font-medium text-gray-900">{form.customerLabel}</span>
                </div>
                {mode === 'create' && (
                  <button
                    type="button"
                    onClick={() => setForm(f => ({ ...f, customerId: '', customerLabel: '' }))}
                    className="text-sm text-gray-500 hover:text-red-600"
                  >
                    Změnit
                  </button>
                )}
              </div>
            ) : (
              <>
                <input
                  type="search"
                  value={customerSearch}
                  onChange={e => setCustomerSearch(e.target.value)}
                  placeholder="Hledat podle jména, e-mailu nebo telefonu…"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
                />
                {customerSearch && (
                  <div className="mt-2 border border-gray-200 rounded-lg max-h-60 overflow-y-auto">
                    {customerLoading && <p className="p-3 text-sm text-gray-500">Hledám…</p>}
                    {!customerLoading && customerResults.length === 0 && (
                      <p className="p-3 text-sm text-gray-500">Žádný klient nenalezen</p>
                    )}
                    {customerResults.map(c => (
                      <button
                        type="button"
                        key={c.id}
                        onClick={() => {
                          setForm(f => ({ ...f, customerId: c.id, customerLabel: c.name }));
                          setCustomerSearch('');
                          setCustomerResults([]);
                        }}
                        className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b last:border-0"
                      >
                        <p className="text-sm font-medium text-gray-900">{c.name}</p>
                        {c.hint && <p className="text-xs text-gray-500">{c.hint}</p>}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Service + worker */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Služba *</label>
              <select
                value={form.serviceId}
                onChange={e => setForm(f => ({ ...f, serviceId: e.target.value }))}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="">— vybrat —</option>
                {services.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Preferovaný zaměstnanec</label>
              <select
                value={form.preferredWorkerId}
                onChange={e => setForm(f => ({ ...f, preferredWorkerId: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="">Kdokoli</option>
                {staff.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Slot mode tabs */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Termín *</label>
            <div className="flex bg-gray-100 rounded-lg p-1 mb-3 w-fit">
              <button
                type="button"
                onClick={() => setForm(f => ({ ...f, slotMode: 'exact' }))}
                className={`px-4 py-1.5 rounded text-sm font-medium transition-colors ${
                  form.slotMode === 'exact' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
                }`}
              >
                Konkrétní termín
              </button>
              <button
                type="button"
                onClick={() => setForm(f => ({ ...f, slotMode: 'range' }))}
                className={`px-4 py-1.5 rounded text-sm font-medium transition-colors ${
                  form.slotMode === 'range' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
                }`}
              >
                Volnější rozsah
              </button>
            </div>

            {form.slotMode === 'exact' ? (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Datum</label>
                  <input
                    type="date"
                    value={form.date}
                    onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Čas</label>
                  <input
                    type="time"
                    value={form.time}
                    onChange={e => setForm(f => ({ ...f, time: e.target.value }))}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Datum od</label>
                    <input
                      type="date"
                      value={form.dateFrom}
                      onChange={e => setForm(f => ({ ...f, dateFrom: e.target.value }))}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Datum do</label>
                    <input
                      type="date"
                      value={form.dateTo}
                      onChange={e => setForm(f => ({ ...f, dateTo: e.target.value }))}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Čas od (volitelné)</label>
                    <input
                      type="time"
                      value={form.timeFrom}
                      onChange={e => setForm(f => ({ ...f, timeFrom: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Čas do (volitelné)</label>
                    <input
                      type="time"
                      value={form.timeTo}
                      onChange={e => setForm(f => ({ ...f, timeTo: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Když čas nevyplníte, znamená to „kdykoli během dne v daném datumu".
                </p>
              </>
            )}
          </div>

          {/* Notes + priority */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Poznámka</label>
            <textarea
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              rows={3}
              placeholder="Detail co řekl klient, podmínky, …"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Priorita</label>
            <input
              type="number"
              value={form.priority}
              onChange={e => setForm(f => ({ ...f, priority: parseInt(e.target.value || '0', 10) }))}
              className="w-32 px-3 py-2 border border-gray-300 rounded-lg"
            />
            <p className="text-xs text-gray-500 mt-1">Vyšší číslo = vyšší priorita při uvolnění slotu.</p>
          </div>

          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded p-3">{error}</div>}

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
            <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
              Zrušit
            </button>
            <button
              type="submit"
              disabled={!canSubmit || submitting}
              className="px-6 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Ukládám…' : mode === 'edit' ? 'Uložit' : 'Přidat'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WaitingListModal;
